import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  HeartIcon,
  EllipsisVerticalIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'
import FolderBreadcrumb from './FolderBreadcrumb'
import SecurityBadges from './SecurityBadges'
import {
  getVideoThumbnail,
  isYouTubeUrl,
  extractYouTubeVideoId,
} from '@/utils/urlUtils'
import { BookmarkedUrl } from '@/types/bookmark'
import toast from 'react-hot-toast'
import { bookmarkApi } from '../../../shared/services/bookmarkApi'
import YouTubeVideoModal from './bookmark/YouTubeVideoModal'
import VideoThumbnail from './url/VideoThumbnail'
import EditUrlModal from './url/EditUrlModal'
import DeleteUrlConfirmModal from './url/DeleteUrlConfirmModal'
import { useTranslation } from 'react-i18next'

// Types - 이제 공통 타입을 사용

interface UrlCardProps {
  url: BookmarkedUrl
  onUpdate: (url: BookmarkedUrl) => void
  onDelete: (urlId: string) => void
  onCopySuccess: (text: string) => void
  onTagClick?: (tagId: string) => void
}

// URL 카드 컴포넌트
const UrlCard: React.FC<UrlCardProps> = ({
  url,
  onUpdate,
  onDelete,
  onCopySuccess,
  onTagClick,
}) => {
  const { t } = useTranslation()
  const [showMenu, setShowMenu] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const queryClient = useQueryClient()

  // 비디오 링크인지 확인
  const isVideo = isYouTubeUrl(url.originalUrl)
  const videoInfo = getVideoThumbnail(url.originalUrl)
  const youtubeVideoId = isVideo ? extractYouTubeVideoId(url.originalUrl) : null

  // 폴더 클릭 핸들러 - 상위 컴포넌트에서 전달받은 폴더 클릭 처리 함수 호출
  const handleFolderClick = (folderId: string) => {
    // 폴더 클릭 시 해당 폴더로 필터링하는 함수
    // BookmarkPage에서 전달받은 함수를 호출하거나 이벤트를 발생시킴
    const event = new CustomEvent('folderNavigate', {
      detail: { folderId },
    })
    window.dispatchEvent(event)
  }

  // 즐겨찾기 토글
  const toggleFavorite = useMutation({
    mutationFn: async () => {
      return await bookmarkApi.toggleFavorite(url._id)
    },
    onSuccess: isFavorite => {
      onUpdate({ ...url, isFavorite })
      queryClient.invalidateQueries({ queryKey: ['bookmarked-urls'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] }) // 태그 쿼리도 무효화
    },
    onError: error => {
      console.error('즐겨찾기 변경 실패:', error)
      toast.error('즐겨찾기 변경에 실패했습니다.')
    },
  })

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      onCopySuccess(text)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleDeleteUrl = () => {
    onDelete(url._id)
    setShowDeleteConfirm(false)
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: url.metadata?.title || url.title || 'Shortened URL',
          text: `Check out this link: ${url.metadata?.title || url.title || 'Shortened URL'}`,
          url: url.shortUrl,
        })
      } catch (err) {
        console.error('Error sharing:', err)
        // Fallback to copy
        copyToClipboard(url.shortUrl)
      }
    } else {
      // Fallback to copy
      copyToClipboard(url.shortUrl)
    }
  }

  const handleViewOriginal = () => {
    window.open(url.originalUrl, '_blank')
  }

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) {
      return url
    }
    return url.substring(0, maxLength - 3) + '...'
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
    >
      {/* YouTube 썸네일이 있는 경우 */}
      {isVideo && videoInfo ? (
        <div className="relative">
          <VideoThumbnail
            url={url.originalUrl}
            title={url.metadata?.title || url.title}
            className="h-48 w-full object-cover"
            onClick={() => youtubeVideoId && setShowVideoModal(true)}
          />
          {/* 비디오 정보 오버레이 */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="mb-2 flex items-center space-x-2 text-white">
              {url.metadata?.favicon && (
                <img
                  src={url.metadata.favicon}
                  alt=""
                  className="h-5 w-5 rounded"
                  onError={e => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold">
                  {url.metadata?.title || url.title || 'Untitled'}
                </h3>
                <p className="truncate text-sm text-gray-200">
                  {truncateUrl(url.originalUrl)}
                </p>
              </div>
            </div>

            {/* 보안 배지 */}
            <SecurityBadges url={url} className="mb-2" />
          </div>

          {/* 즐겨찾기 및 메뉴 버튼 */}
          <div className="absolute right-4 top-4 flex items-center space-x-2">
            <button
              onClick={() => toggleFavorite.mutate()}
              disabled={toggleFavorite.isPending}
              className={`rounded-full p-2 backdrop-blur-sm transition-colors ${
                url.isFavorite
                  ? 'bg-red-500/80 text-white'
                  : 'bg-black/40 text-white hover:bg-red-500/80'
              }`}
            >
              {url.isFavorite ? (
                <HeartSolidIcon className="h-5 w-5" />
              ) : (
                <HeartIcon className="h-5 w-5" />
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/60"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  <button
                    onClick={() => {
                      setShowEditModal(true)
                      setShowMenu(false)
                    }}
                    className="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <PencilIcon className="mr-2 h-4 w-4" />
                    {t('link.edit')}
                  </button>
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(true)
                      setShowMenu(false)
                    }}
                    className="flex w-full items-center px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <TrashIcon className="mr-2 h-4 w-4" />
                    {t('link.delete')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* 일반 링크 헤더 */
        <div className="border-b border-gray-200 p-6 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex min-w-0 flex-1 items-center space-x-3">
              {url.metadata?.favicon && (
                <img
                  src={url.metadata.favicon}
                  alt=""
                  className="h-5 w-5 rounded"
                  onError={e => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold text-gray-900 dark:text-white">
                  {url.metadata?.title || url.title || 'Untitled'}
                </h3>
                <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                  {truncateUrl(url.originalUrl)}
                </p>

                {/* 보안 배지 */}
                <SecurityBadges url={url} className="mt-2" />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* 즐겨찾기 버튼 */}
              <button
                onClick={() => toggleFavorite.mutate()}
                disabled={toggleFavorite.isPending}
                className={`rounded-md p-2 transition-colors ${
                  url.isFavorite
                    ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                    : 'text-gray-400 hover:bg-gray-50 hover:text-red-500 dark:hover:bg-gray-700'
                }`}
              >
                {url.isFavorite ? (
                  <HeartSolidIcon className="h-5 w-5" />
                ) : (
                  <HeartIcon className="h-5 w-5" />
                )}
              </button>

              {/* 메뉴 버튼 */}
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="rounded-md p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-700"
                >
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </button>

                {showMenu && (
                  <div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                    <button
                      onClick={() => {
                        setShowEditModal(true)
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <PencilIcon className="mr-2 h-4 w-4" />
                      {t('link.edit')}
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(true)
                        setShowMenu(false)
                      }}
                      className="flex w-full items-center px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="mr-2 h-4 w-4" />
                      {t('link.delete')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 카드 내용 */}
      <div className="p-6">
        {/* 메모 */}
        {url.notes && (
          <div className="mb-4">
            <p className="rounded-md bg-gray-50 p-3 text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-400">
              {url.notes}
            </p>
          </div>
        )}

        {/* 태그 */}
        {url.tags && url.tags.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {url.tags.map(tag => (
              <button
                key={tag._id}
                onClick={() => onTagClick?.(tag._id)}
                className="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-xs font-medium text-white transition-opacity hover:opacity-80"
                style={{ backgroundColor: tag.color }}
                title={`"${tag.name}" 태그로 필터링`}
              >
                <TagIcon className="mr-1 h-3 w-3" />
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {/* 폴더 정보 - 계층 구조 표시 */}
        {url.folderId && (
          <div className="mb-4">
            <FolderBreadcrumb
              folder={url.folderId}
              onFolderClick={handleFolderClick}
              className="text-gray-600 dark:text-gray-400"
            />
          </div>
        )}

        {/* 단축 URL 정보 */}
        <div className="mb-4 rounded-md bg-gray-50 p-3 dark:bg-gray-700">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Short URL
              </p>
              <code className="block truncate font-mono text-sm text-gray-900 dark:text-white">
                {url.shortUrl}
              </code>
            </div>
            <button
              onClick={() => copyToClipboard(url.shortUrl)}
              className="ml-2 rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
              title={t('link.copy')}
            >
              <ClipboardIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* 통계 및 액션 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <div className="flex items-center">
              <EyeIcon className="mr-1 h-4 w-4" />
              {url.clickCount.toLocaleString()}
            </div>
            <div>{new Date(url.createdAt).toLocaleDateString()}</div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleViewOriginal}
              className="rounded-md bg-gray-100 px-3 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {isVideo ? t('link.watchVideo') : '원본 보기'}
            </button>
            <button
              onClick={handleNativeShare}
              className="rounded-md bg-blue-100 px-3 py-1 text-xs text-blue-700 transition-colors hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40"
            >
              {t('link.share')}
            </button>
          </div>
        </div>
      </div>

      {/* 편집 모달 */}
      {showEditModal && (
        <EditUrlModal
          url={url}
          onClose={() => setShowEditModal(false)}
          onUpdate={onUpdate}
        />
      )}

      {/* YouTube 비디오 모달 */}
      <AnimatePresence>
        {showVideoModal && youtubeVideoId && (
          <YouTubeVideoModal
            videoId={youtubeVideoId}
            title={url.metadata?.title || url.title}
            onClose={() => setShowVideoModal(false)}
          />
        )}
      </AnimatePresence>

      {/* URL 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <DeleteUrlConfirmModal
          url={url}
          onConfirm={handleDeleteUrl}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </motion.div>
  )
}

export default UrlCard
